import { LINE_GROUP_LABELS } from "@shared/roi/defaults";
import {
  LINE_GROUPS,
  type LineGroup,
  type ScenarioModel,
  type YearResult,
} from "@shared/roi/types";
import { packageColorVar } from "@client/lib/packageColors";

/**
 * Turning a projection into the "where the money goes" stacks.
 *
 * This is the question the ZNMS page could not answer: with ten line items and
 * four packages, *which* of them is the money? So revenue is split by package
 * and cost by line-item group, with each package's own delivery cost carrying
 * that package's colour on the cost side too — a package's hue means the same
 * thing in both stacks.
 *
 * The line-item groups cannot borrow the package ramp without claiming a
 * relationship that isn't there, so they take the direction's own hue at
 * descending tints: teal for revenue, amber for cost, exactly as everywhere
 * else in the app (docs/09-theme.md#charts).
 *
 * Pure, and separated from the component, because "the stack sums to the year's
 * total" is an arithmetic claim worth testing without a DOM.
 */

const GROUP_TINTS = [1, 0.78, 0.6, 0.45, 0.33, 0.24];

export type BreakdownSide = "revenue" | "cost";

export interface BreakdownSeries {
  key: string;
  label: string;
  color: string;
  opacity: number;
  side: BreakdownSide;
  /** What it is, for the legend's grouping: a package or a line-item group. */
  kind: "package" | "group";
  /** The package id or the `LineGroup` this series draws. */
  sourceId: string;
}

export interface BreakdownSlice {
  series: BreakdownSeries;
  value: number;
}

export interface BreakdownYear {
  year: number;
  revenue: BreakdownSlice[];
  cost: BreakdownSlice[];
  revenueTotal: number;
  costTotal: number;
}

export interface Breakdown {
  years: BreakdownYear[];
  /** Only the series that are non-zero somewhere, in draw order. */
  series: BreakdownSeries[];
  /** The tallest column across both sides, for the shared money axis. */
  max: number;
}

const groupTint = (group: LineGroup): number =>
  GROUP_TINTS[LINE_GROUPS.indexOf(group)] ?? GROUP_TINTS[GROUP_TINTS.length - 1] ?? 1;

export function buildBreakdown(model: ScenarioModel, rows: YearResult[]): Breakdown {
  const packages = [...model.packages].sort((a, b) => a.sortOrder - b.sortOrder);

  const packageSeries = (side: BreakdownSide): BreakdownSeries[] =>
    packages.map((pkg) => ({
      key: `${side}:pkg:${pkg.id}`,
      label: pkg.name.trim() || "Untitled package",
      color: packageColorVar(pkg.colorIndex),
      opacity: 1,
      side,
      kind: "package" as const,
      sourceId: pkg.id,
    }));

  const groupSeries = (side: BreakdownSide): BreakdownSeries[] =>
    LINE_GROUPS.map((group) => ({
      key: `${side}:grp:${group}`,
      label: LINE_GROUP_LABELS[group],
      color: side === "revenue" ? "var(--roi-revenue)" : "var(--roi-cost)",
      opacity: groupTint(group),
      side,
      kind: "group" as const,
      sourceId: group,
    }));

  // Packages first on both sides, so the two stacks of a year line up by hue
  // from the baseline upwards and a package's share is comparable between them.
  const revenueSeries = [...packageSeries("revenue"), ...groupSeries("revenue")];
  const costSeries = [...packageSeries("cost"), ...groupSeries("cost")];

  const byGroup = (row: YearResult, wanted: BreakdownSide): Map<LineGroup, number> => {
    const totals = new Map<LineGroup, number>();
    for (const item of model.lineItems) {
      const signed = row.byLineItem[item.id] ?? 0;
      // The engine signs a line item's contribution rather than reporting its
      // direction, so a revenue item with a negative amount lands on the cost
      // side — which is what it does to the year, and so what the chart shows.
      const value = wanted === "revenue" ? Math.max(0, signed) : Math.max(0, -signed);
      if (value === 0) continue;
      totals.set(item.group, (totals.get(item.group) ?? 0) + value);
    }
    return totals;
  };

  const years: BreakdownYear[] = rows.map((row) => {
    const revenueGroups = byGroup(row, "revenue");
    const costGroups = byGroup(row, "cost");

    const slice = (series: BreakdownSeries[], value: (s: BreakdownSeries) => number) =>
      series
        .map((s) => ({ series: s, value: Math.max(0, value(s)) }))
        .filter((entry) => entry.value > 0);

    const revenue = slice(revenueSeries, (s) =>
      s.kind === "package"
        ? (row.byPackage[s.sourceId]?.revenue ?? 0)
        : (revenueGroups.get(s.sourceId as LineGroup) ?? 0),
    );
    const cost = slice(costSeries, (s) =>
      s.kind === "package"
        ? (row.byPackage[s.sourceId]?.cost ?? 0)
        : (costGroups.get(s.sourceId as LineGroup) ?? 0),
    );

    return {
      year: row.year,
      revenue,
      cost,
      // Summed from the slices rather than taken from the row: a negative
      // component is dropped above, and a total that disagrees with the bars
      // above it is worse than a total that is a shade off the KPI strip.
      revenueTotal: revenue.reduce((sum, entry) => sum + entry.value, 0),
      costTotal: cost.reduce((sum, entry) => sum + entry.value, 0),
    };
  });

  const used = new Set<string>();
  for (const year of years) {
    for (const entry of [...year.revenue, ...year.cost]) used.add(entry.series.key);
  }

  return {
    years,
    series: [...revenueSeries, ...costSeries].filter((s) => used.has(s.key)),
    max: years.reduce((m, year) => Math.max(m, year.revenueTotal, year.costTotal), 0),
  };
}
